import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { Ref, Button, Icon } from "semantic-ui-react";
import StickyMenu from "~/components/StickyMenu";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";
import UiMeet3dNotificationForm from "~/modules/UiMeet3d/components/UiMeet3dNotificationForm";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import UiMeet3dNotificationFormStore from "~/stores/UiMeet3d/UiMeet3dNotificationFormStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import UiMeet3dLayoutWrapper from "~/modules/UiMeet3d/components/UiMeet3dLayoutWrapper";
import UiMeet3dNav from "~/modules/UiMeet3d/constants/UiMeet3dNav";

@observer
class UiMeet3dNotificationSettingsPage extends Component {
  ref = null;

  @observable uiMeet3dNotificationFormStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    this.ref = createRef();
    document.title = `${t("uiMeet3d")} - ${t("clientPanelMisc:notifications")}`;

    this.uiMeet3dNotificationFormStore = new UiMeet3dNotificationFormStore();
  }

  componentDidMount() {
    const { actualUserStore } = this.props;
    const { actualUser } = actualUserStore;
    this.bindForm(actualUser);
  }

  onSubmit() {
    const { showError, toastMessageStore, t, actualUserStore } = this.props;
    const { model } = this.uiMeet3dNotificationFormStore;
    const {
      notificationFirstTimeMovementEmail,
      notificationFirstTimeMovementContactPhone,
      notificationFirstTimeMovementWebhook,
      notificationLeadGenerationEmail,
      notificationLeadGenerationContactPhone,
      notificationLeadGenerationWebhook,
      notificationMeetingRequestEmail,
      notificationMeetingRequestContactPhone,
      notificationMeetingRequestWebhook,
      notifications,
      notificationHourRanges,
      contactInformationUseDefault,
      contactInformationEmail,
      contactInformationContactPhone,
      contactInformationWebhook,
    } = model;

    const formActualUser = {
      uiMeet3dSettings: {
        notificationEvents: {
          firstTimeMovement: {
            email: notificationFirstTimeMovementEmail,
            contactPhone: notificationFirstTimeMovementContactPhone,
            webhook: notificationFirstTimeMovementWebhook,
          },
          leadGeneration: {
            email: notificationLeadGenerationEmail,
            contactPhone: notificationLeadGenerationContactPhone,
            webhook: notificationLeadGenerationWebhook,
          },
          meetingRequest: {
            email: notificationMeetingRequestEmail,
            contactPhone: notificationMeetingRequestContactPhone,
            webhook: notificationMeetingRequestWebhook,
          },
        },
        notificationHours: [{}, {}, {}, {}, {}, {}, {}],
        contactInformation: {
          useDefault: contactInformationUseDefault,
          email: contactInformationEmail !== "" ? contactInformationEmail : null,
          contactPhone: contactInformationContactPhone !== "" ? contactInformationContactPhone : null,
          webhook: contactInformationWebhook !== "" ? contactInformationWebhook : null,
        },
      },
    };

    for (let i = 0; i < 7; i += 1) {
      formActualUser.uiMeet3dSettings.notificationHours[i] = {
        notification: notifications[i],
        hourRanges: this.decodeNotificationHourRanges(notificationHourRanges[i]),
      };
    }

    actualUserStore
      .save(formActualUser)
      .then(() => {
        const { actualUser } = actualUserStore;

        this.bindForm(actualUser);

        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
      })
      .catch(error => {
        if (error.status === "error") {
          showError(error.message);
        }
      });
  }

  bindForm(actualUser) {
    let notificationFirstTimeMovementEmail = true;
    let notificationFirstTimeMovementContactPhone = true;
    let notificationFirstTimeMovementWebhook = true;
    let notificationLeadGenerationEmail = true;
    let notificationLeadGenerationContactPhone = true;
    let notificationLeadGenerationWebhook = true;
    let notificationMeetingRequestEmail = true;
    let notificationMeetingRequestContactPhone = true;
    let notificationMeetingRequestWebhook = true;
    const notifications = [false, false, false, false, false, false, false];
    const notificationHourRanges = [null, null, null, null, null, null, null];
    let contactInformationUseDefault = true;
    let contactInformationEmail = "";
    let contactInformationContactPhone = "";
    let contactInformationWebhook = "";

    if (typeof actualUser.uiMeet3dSettings.notificationEvents !== "undefined") {
      const { notificationEvents } = actualUser.uiMeet3dSettings;
      notificationFirstTimeMovementEmail = notificationEvents.firstTimeMovement.email;
      notificationFirstTimeMovementContactPhone = notificationEvents.firstTimeMovement.contactPhone;
      notificationFirstTimeMovementWebhook = notificationEvents.firstTimeMovement.webhook;
      notificationLeadGenerationEmail = notificationEvents.leadGeneration.email;
      notificationLeadGenerationContactPhone = notificationEvents.leadGeneration.contactPhone;
      notificationLeadGenerationWebhook = notificationEvents.leadGeneration.webhook;
      notificationMeetingRequestEmail = notificationEvents.meetingRequest.email;
      notificationMeetingRequestContactPhone = notificationEvents.meetingRequest.contactPhone;
      notificationMeetingRequestWebhook = notificationEvents.meetingRequest.webhook;
    }

    for (let i = 0; i < actualUser.uiMeet3dSettings.notificationHours.length; i += 1) {
      notifications[i] = actualUser.uiMeet3dSettings.notificationHours[i].notification;
      notificationHourRanges[i] = this.encodeNotificationHourRanges(
        actualUser.uiMeet3dSettings.notificationHours[i].hourRanges,
      );
    }

    if (typeof actualUser.uiMeet3dSettings.contactInformation !== "undefined") {
      const { contactInformation } = actualUser.uiMeet3dSettings;
      contactInformationUseDefault = contactInformation.useDefault;
      contactInformationEmail = contactInformation.email ?? "";
      contactInformationContactPhone = contactInformation.contactPhone ?? "";
      contactInformationWebhook = contactInformation.webhook ?? "";
    }

    const initialModel = {
      notificationFirstTimeMovementEmail,
      notificationFirstTimeMovementContactPhone,
      notificationFirstTimeMovementWebhook,
      notificationLeadGenerationEmail,
      notificationLeadGenerationContactPhone,
      notificationLeadGenerationWebhook,
      notificationMeetingRequestEmail,
      notificationMeetingRequestContactPhone,
      notificationMeetingRequestWebhook,
      notifications,
      notificationHourRanges,
      contactInformationUseDefault,
      contactInformationEmail,
      contactInformationContactPhone,
      contactInformationWebhook,
    };

    this.uiMeet3dNotificationFormStore = new UiMeet3dNotificationFormStore(initialModel);
  }

  encodeNotificationHourRanges(notificationHourRanges) {
    let encodedNotificationHourRanges = "";

    if (notificationHourRanges === null) {
      return encodedNotificationHourRanges;
    }

    for (let i = 0; i < notificationHourRanges.length; i += 1) {
      if (encodedNotificationHourRanges !== "") {
        encodedNotificationHourRanges += ",";
      }

      const fromString = notificationHourRanges[i].from.toString();
      const toString = notificationHourRanges[i].to.toString();

      encodedNotificationHourRanges += `${fromString}-${toString}`;
    }

    return encodedNotificationHourRanges;
  }

  decodeNotificationHourRanges(encodedNotificationHourRanges, fromToFormat = "number") {
    if (encodedNotificationHourRanges === "") {
      return null;
    }

    const notificationHourRanges = [];

    const parts = encodedNotificationHourRanges.split(",");

    for (let i = 0; i < parts.length; i += 1) {
      const fromTo = parts[i].split("-");

      notificationHourRanges.push({
        from: fromToFormat === "number" ? parseInt(fromTo[0], 10) : fromTo[0].toString(),
        to: fromToFormat === "number" ? parseInt(fromTo[1], 10) : fromTo[1].toString(),
      });
    }

    return notificationHourRanges;
  }

  render() {
    const { t, toastMessageStore, actualUserStore } = this.props;
    const { isLoading, errorMessages, actualUser, isSaving } = actualUserStore;
    const { model, isDirty } = this.uiMeet3dNotificationFormStore;
    const { notifications, notificationHourRanges } = model;

    if (isLoading || typeof notifications === "undefined" || typeof notificationHourRanges === "undefined") {
      return <ContentLoader />;
    }

    return (
      <>
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <div className="side-page-outer-container" style={{ marginTop: "0" }}>
          <Ref innerRef={this.ref}>
            <div>
              <StickyMenu
                contextRef={this.ref}
                headerText={t("clientPanelMisc:notifications")}
                button={
                  <div className="side-page-save-btn-wrap">
                    <Button primary icon disabled={!isDirty} onClick={() => this.onSubmit()}>
                      <Icon name="save" />
                      {t("saveChanges")}
                    </Button>
                  </div>
                }
              />
              <UiMeet3dNotificationForm
                defaultTimezone={
                  actualUserStore instanceof ProviderStore
                    ? actualUser.defaultTimezone
                    : actualUser.defaultTimezoneCoalesced
                }
                contactPhone={actualUser.contactPhone}
                email={actualUser.email}
                formStore={this.uiMeet3dNotificationFormStore}
                validationErrors={typeof errorMessages === "object" ? errorMessages : null}
                encodeNotificationHourRanges={items => this.encodeNotificationHourRanges(items)}
                decodeNotificationHourRanges={(encodedNotificationHourRanges, fromToFormat) =>
                  this.decodeNotificationHourRanges(encodedNotificationHourRanges, fromToFormat)
                }
              />
            </div>
          </Ref>
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

UiMeet3dNotificationSettingsPage.propTypes = {
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  showError: PropTypes.func.isRequired,
};

export default withTranslation()(
  ErrorWrapper(UiMeet3dLayoutWrapper(UiMeet3dNotificationSettingsPage, UiMeet3dNav.NOTIFICATIONS)),
);
